import { useCallback } from 'react';

import { plural, t, Trans } from '@lingui/macro';

import Download from '@decisiv/iconix/lib/components/Download';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import { useBulkCreateDepotContext } from '../context/BulkCreateDepotContext';
import { CountsContainer } from './styles';

const Summary = () => {
  const { summary, reportUrl, failedToResolve } = useBulkCreateDepotContext();

  const handleDownloadResultsClick = useCallback(() => {
    // Defensive guard: the button is disabled when reportUrl is absent, so
    // this branch is unreachable via the UI.
    /* istanbul ignore next -- @preserve */
    if (!reportUrl) return;

    window.location.assign(reportUrl);
  }, [reportUrl]);

  // Polling stopped without a resolved server outcome (repeated errors / timeout).
  // Don't render "0 added / 0 failed" — that reads as a completed no-op run.
  if (failedToResolve || !summary) {
    return (
      <Flex flexDirection="column">
        <H3>
          <Trans>Summary</Trans>
        </H3>
        <Flex marginTop={1}>
          <Alert intent="warning" title={t`Import status unavailable`}>
            <Trans>
              We couldn&apos;t confirm the result of your upload. It may still
              be processing — check the depots list in a few minutes before
              retrying.
            </Trans>
          </Alert>
        </Flex>
      </Flex>
    );
  }

  const added = summary.successful ?? 0;
  const failed = summary.failed ?? 0;

  return (
    <Flex flexDirection="column">
      <H3>
        <Trans>Summary</Trans>
      </H3>

      <Flex flex={1} justifyContent="space-between">
        <CountsContainer>
          <P shade={1}>
            {plural(added, { one: '# depot added', other: '# depots added' })}
          </P>
          <P shade={1}>
            {plural(failed, {
              one: '# depot failed',
              other: '# depots failed',
            })}
          </P>
        </CountsContainer>
        <Flex marginTop={1} alignSelf="flex-end">
          <Button
            text={t`Download Report`}
            size="small"
            kind="secondary"
            icon={Download}
            disabled={!reportUrl}
            onClick={handleDownloadResultsClick}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Summary;
